<template>
  <div class="folder-list-component">
    <div class="folder-list-controls">
      <div class="folder-list-search">
        <label class="sr-only" for="folder-list-search-input">{{ labels.searchLabel }}</label>
        <input
          id="folder-list-search-input"
          v-model="searchText"
          type="search"
          class="form-control form-control-sm"
          autocomplete="off"
          data-testid="folder-list-search"
          :placeholder="labels.searchPlaceholder"
        >
      </div>
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        data-testid="folder-list-expand-all"
        :disabled="isSearching"
        @click="expandAllFolders"
      >{{ labels.expandAll }}</button>
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        data-testid="folder-list-collapse-all"
        :disabled="isSearching"
        @click="collapseAllFolders"
      >{{ labels.collapseAll }}</button>
      <span
        v-if="isSearching"
        class="folder-list-count"
        data-testid="folder-list-count"
        aria-live="polite"
      >{{ countText }}</span>
    </div>

    <div class="table-responsive">
      <table class="table folder-list-table dashboard-table">
        <thead>
          <tr>
            <th scope="col" class="folder-col-id">{{ labels.colId }}</th>
            <th scope="col" class="folder-col-name">{{ labels.colName }}</th>
            <th scope="col" class="folder-col-sort">{{ labels.colSortOrder }}</th>
            <th v-if="isRootAdmin" scope="col">{{ labels.colCustomer }}</th>
            <th scope="col" class="folder-col-created">{{ labels.colCreatedAt }}</th>
            <th scope="col" class="folder-col-actions actions-column"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isSearching && !visibleRows.length">
            <td :colspan="columnCount" class="dashboard-table-empty" data-testid="folder-list-search-empty">{{ labels.searchEmpty }}</td>
          </tr>
          <tr
            v-for="row in visibleRows"
            :key="row.folder.id"
            data-testid="folder-list-row"
            :class="{ 'folder-list-root-row': row.level === 1 }"
            :data-folder-id="row.folder.id"
            :data-depth="row.folder.depth"
          >
            <td class="folder-col-id dashboard-meta-cell">{{ row.folder.id }}</td>
            <td class="folder-col-name folder-name-cell dashboard-main-cell">
              <span class="folder-name-text" :style="{ '--folder-depth': row.level - 1 }">
                <button
                  v-if="row.hasChildren && !isSearching"
                  type="button"
                  class="folder-tree-toggle"
                  data-testid="folder-list-toggle"
                  :aria-expanded="String(!isCollapsed(row.folder.id))"
                  :aria-label="toggleLabel(row.folder)"
                  @click="toggleFolder(row.folder.id)"
                >
                  <i class="fas" :class="isCollapsed(row.folder.id) ? 'fa-caret-right' : 'fa-caret-down'" aria-hidden="true"></i>
                </button>
                <span v-else class="folder-tree-toggle-spacer" aria-hidden="true"></span>
                <i class="fas fa-folder folder-tree-icon" aria-hidden="true"></i>
                <span class="folder-tree-label" :class="{ 'is-root-folder': row.folder.depth === 1 }"><template
                  v-for="(segment, index) in segmentsOf(row.folder)"
                  :key="index"
                ><mark v-if="segment.hit" class="folder-search-hit">{{ segment.text }}</mark><template v-else>{{ segment.text }}</template></template></span>
              </span>
            </td>
            <td class="folder-col-sort">
              <div class="dashboard-sort-cell">
                <span class="dashboard-sort-value">{{ row.folder.sort_order }}</span>
                <span class="dashboard-sort-actions">
                  <a :href="urlFor('up', row.folder.id)" class="dashboard-sort-link" aria-label="sort-up" data-testid="folder-list-sort-up">↑</a>
                  <a :href="urlFor('down', row.folder.id)" class="dashboard-sort-link" aria-label="sort-down" data-testid="folder-list-sort-down">↓</a>
                </span>
              </div>
            </td>
            <td v-if="isRootAdmin" class="dashboard-meta-cell">{{ row.folder.customer ?? '-' }}</td>
            <td class="folder-col-created dashboard-meta-cell">{{ row.folder.created_at }}</td>
            <td class="folder-col-actions actions-column">
              <a :href="urlFor('edit', row.folder.id)" class="btn btn-secondary btn-sm ml-1" data-testid="folder-list-edit">{{ labels.edit }}</a>
              <a
                :href="urlFor('remove', row.folder.id)"
                class="btn btn-danger btn-sm ml-1"
                data-testid="folder-list-delete"
                @click="confirmDelete"
              >{{ labels.delete }}</a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useFolderTree } from '../dashboard/useFolderTree';

// 開閉の保存先は TOP 左のツリーと分ける
const STORAGE_KEY = 'exa_folder_list_collapsed_folders';

const props = defineProps({
  // 行 (id, name, parent_id (TOP は 0), depth, sort_order, created_at, customer (root_admin のみ))。兄弟の並び済み
  folders: {
    type: Array,
    required: true,
  },
  // edit / remove / up / down の URL の雛形 ('__ID__' をフォルダ id に置き換える)
  urlTemplates: {
    type: Object,
    required: true,
  },
  isRootAdmin: {
    type: Boolean,
    default: false,
  },
  // 画面の文言 (Blade が lang から渡す)
  labels: {
    type: Object,
    required: true,
  },
});

const searchText = ref('');
const selectedFolderId = ref(null);

// 他の画面から来たとき・再読み込み・直接開いたときは全折りたたみ。
// 直前のページが /dashboard/folders 配下 (↑ ↓・編集・削除・追加の後のリダイレクト) のときだけ開閉を戻す
const { topLevelFolders, getChildren, isCollapsed, toggleFolder, expandAllFolders, collapseAllFolders } = useFolderTree(
  computed(() => props.folders),
  selectedFolderId,
  {
    persist: true,
    storageKey: STORAGE_KEY,
    isInternalPathname: (pathname) => pathname.startsWith('/dashboard/folders'),
  }
);

const columnCount = computed(() => (props.isRootAdmin ? 6 : 5));

const normalizeForSearch = (value) => String(value ?? '').normalize('NFC').toLowerCase();

const query = computed(() => normalizeForSearch(searchText.value));
const isSearching = computed(() => query.value !== '');

// 名前の比較用 (NFC・小文字)。フォルダ一覧が変わったときだけ作り直す
const searchKeys = computed(() => {
  const map = new Map();
  props.folders.forEach((folder) => {
    map.set(folder.id, normalizeForSearch(folder.name));
  });
  return map;
});

// 検索中: 名前が合ったフォルダ
const matchedIds = computed(() => {
  const ids = new Set();
  if (!isSearching.value) return ids;
  const needle = query.value;
  props.folders.forEach((folder) => {
    if (searchKeys.value.get(folder.id).includes(needle)) {
      ids.add(folder.id);
    }
  });
  return ids;
});

// 検索中: 合ったフォルダとその親たち
const keptIds = computed(() => {
  const kept = new Set();
  if (!isSearching.value) return kept;
  const byId = new Map();
  props.folders.forEach((folder) => byId.set(folder.id, folder));
  matchedIds.value.forEach((id) => {
    let current = byId.get(id);
    while (current && !kept.has(current.id)) {
      kept.add(current.id);
      current = current.parent_id ? byId.get(current.parent_id) : null;
    }
  });
  return kept;
});

// 表に出す行 (深さ優先)。検索中は保存済みの開閉 (collapsedFolders) を見ず、合ったフォルダと親たちを全部開いて出す
const visibleRows = computed(() => {
  const rows = [];
  const searching = isSearching.value;
  const kept = keptIds.value;
  const walk = (folder, level) => {
    const children = getChildren(folder.id);
    rows.push({ folder, level, hasChildren: children.length > 0 });
    if (!searching && isCollapsed(folder.id)) return;
    children.forEach((child) => {
      if (!searching || kept.has(child.id)) {
        walk(child, level + 1);
      }
    });
  };
  topLevelFolders.value.forEach((folder) => {
    if (!searching || kept.has(folder.id)) {
      walk(folder, 1);
    }
  });
  return rows;
});

const countText = computed(() => props.labels.searchCount.replace(':count', String(matchedIds.value.size)));

const toggleLabel = (folder) => {
  const template = isCollapsed(folder.id) ? props.labels.toggleOpen : props.labels.toggleClose;
  return template.replace(':name', () => folder.name);
};

// 合った文字を強調するための分割。比較用の文字列と長さが違う (大文字小文字の変換で長さが変わる) ときは強調しない
const segmentsOf = (folder) => {
  const name = String(folder.name);
  if (!isSearching.value || !matchedIds.value.has(folder.id)) {
    return [{ text: name, hit: false }];
  }
  const key = searchKeys.value.get(folder.id);
  if (key.length !== name.length) {
    return [{ text: name, hit: false }];
  }
  const needle = query.value;
  const segments = [];
  let from = 0;
  let index = key.indexOf(needle, from);
  while (index !== -1) {
    if (index > from) {
      segments.push({ text: name.slice(from, index), hit: false });
    }
    segments.push({ text: name.slice(index, index + needle.length), hit: true });
    from = index + needle.length;
    index = key.indexOf(needle, from);
  }
  if (from < name.length) {
    segments.push({ text: name.slice(from), hit: false });
  }
  return segments;
};

const urlFor = (kind, id) => String(props.urlTemplates[kind] ?? '').replace('__ID__', String(id));

const confirmDelete = (event) => {
  if (!window.confirm(props.labels.confirmDelete)) {
    event.preventDefault();
  }
};
</script>
